import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEvent, getImageUrl, updateEvent } from '../services/api';
import { useAuth } from '../context/AuthContext';

const categories = [
  'wedding', 'birthday', 'corporate', 'conference',
  'party', 'graduation', 'family', 'sports', 'concert', 'other'
];

const categoryLabels = {
  wedding: 'Mariage',
  birthday: 'Anniversaire',
  corporate: 'Entreprise',
  conference: 'Conférence',
  party: 'Fête',
  graduation: 'Remise de diplôme',
  family: 'Famille',
  sports: 'Sports',
  concert: 'Concert',
  other: 'Autre'
};

const EditEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    category: 'wedding',
    description: '',
    event_date: '',
    location: '',
    visibility: 'public',
    access_code: '',
    selfie_search_enabled: 0,
  });
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await getEvent(eventId);
        const ev = response.event || response.data?.event || response;

        if (Number(ev.photographer_id) !== Number(user?.id)) {
          setForbidden(true);
          setError('Only the photographer who owns this event can edit it.');
          return;
        }

        setFormData({
          name: ev.name || '',
          category: ev.category || 'wedding',
          description: ev.description || '',
          event_date: ev.event_date ? ev.event_date.split('T')[0] : '',
          location: ev.location || '',
          visibility: ev.visibility || 'public',
          access_code: ev.access_code || '',
          selfie_search_enabled: ev.selfie_search_enabled ? 1 : 0,
        });
        setCoverPhotoPreview(ev.cover_photo ? getImageUrl(ev.cover_photo) : '');
      } catch (err) {
        setError(err.message || "Impossible de charger l'événement");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, user?.id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCoverPhotoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setCoverPhotoFile(file);
    setCoverPhotoPreview(file ? URL.createObjectURL(file) : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let payload = formData;

      if (coverPhotoFile) {
        payload = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          payload.append(key, value ?? '');
        });
        payload.append('cover_photo', coverPhotoFile);
      }

      await updateEvent(eventId, payload);
      navigate(`/event/${eventId}/browse`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Impossible de mettre à jour l'événement");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement de l'événement...</p>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <div className="error-message" style={{ maxWidth: '640px', margin: '0 auto 1.5rem' }}>
          {error}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/event/${eventId}/browse`)}
          className="btn-submit"
          style={{ maxWidth: '220px' }}
        >
          Back to Gallery
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="auth-container" style={{ maxWidth: '600px' }}>
        <h2 style={{ textAlign: 'center', color: 'var(--primary-orange)' }}>
          Modifier l'événement
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--gray)', marginBottom: '2rem' }}>
          Mettez à jour les détails de votre événement
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom de l'événement *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="ex. Mariage Durand 2024"
            />
          </div>

          <div className="form-group">
            <label>Catégorie de l'événement *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {categoryLabels[cat] || cat}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Photo de couverture de l'événement</label>
            {coverPhotoPreview && (
              <img
                src={coverPhotoPreview}
                alt="Aperçu de la couverture de l'événement"
                style={{
                  width: '100%',
                  height: '220px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginBottom: '0.75rem',
                  border: '1px solid var(--border)',
                }}
              />
            )}
            <input
              type="file"
              name="cover_photo"
              accept="image/*"
              onChange={handleCoverPhotoChange}
            />
          </div>

          <div className="form-group">
            <label>Visibilité *</label>
            <select
              name="visibility"
              value={formData.visibility}
              onChange={handleChange}
              required
            >
              <option value="public">Public - Tout le monde peut voir</option>
              <option value="unlisted">Non listé - Uniquement avec le lien</option>
              <option value="private">Privé - Code d'accès requis</option>
            </select>
          </div>

          {formData.visibility === 'private' && (
            <div className="form-group">
              <label>Code d'accès</label>
              <input
                type="text"
                name="access_code"
                value={formData.access_code}
                onChange={handleChange}
                placeholder="Saisissez le code d'accès pour cet événement"
              />
            </div>
          )}

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="selfie_search_enabled"
                checked={Boolean(formData.selfie_search_enabled)}
                onChange={(e) => setFormData({ ...formData, selfie_search_enabled: e.target.checked ? 1 : 0 })}
                disabled={saving}
                style={{ width: 'auto' }}
              />{' '}Activer la recherche de photos par selfie
            </label>
            <small>Les invités pourront retrouver leurs photos grâce à la reconnaissance faciale. Ce choix s'applique à tout l'événement, y compris aux photos déjà importées.</small>
          </div>

          <div className="form-group">
            <label>Lieu</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Paris, France"
            />
          </div>

          <div className="form-group">
            <label>Date de l'événement</label>
            <input
              type="date"
              name="event_date"
              value={formData.event_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Ajoutez des détails supplémentaires sur l'événement..."
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                flex: 1, padding: '0.9rem',
                background: 'var(--surface-2)', color: 'var(--dark-gray)',
                border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontWeight: '600', fontSize: '1rem',
              }}
            >
              Annuler
            </button>
            <button type="submit" className="btn-submit" disabled={saving} style={{ flex: 2 }}>
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEvent;
